import { api, type JobView } from "../api";
import { EvidenceDownloadButton, type BundleStatus } from "./EvidenceDownloadButton";

interface Props {
  job: JobView;
  expired: boolean;
  bundleStatus: BundleStatus;
}

// Order MUST match `Stage::ALL` in `crates/core/src/stage.rs`. The
// `extract` stage was previously missing here, which made `stageState`
// fall through to "pending" for every row whenever the worker was
// briefly in `extract`. Finalize is intentionally omitted — it's a
// post-pipeline cleanup with nothing user-visible.
const STAGES = [
  { key: "ingest", label: "Ingest video" },
  { key: "transcribe", label: "Transcribe audio" },
  { key: "extract", label: "Extract frames" },
  { key: "annotate", label: "Annotate frames" },
  { key: "fuse", label: "Fuse timeline" },
  { key: "author", label: "Author OpenSpec change" },
  { key: "validate", label: "Validate" },
  { key: "assemble", label: "Assemble" },
] as const;

const TERMINAL = new Set([
  "succeeded",
  "succeededwithwarnings",
  "failed",
  "cancelled",
]);

export function OverviewTab({ job, expired, bundleStatus }: Props) {
  const status = job.status.toLowerCase();
  const stagePct = job.stage_progress?.pct ?? 0;
  const currentStage = job.stage_progress?.stage?.toLowerCase() ?? null;
  const succeeded = status === "succeeded" || status === "succeededwithwarnings";
  const failed = status === "failed";

  return (
    <div>
      <div className="overview-meta">
        <Meta label="Status" value={job.status} />
        <Meta label="Cost" value={job.cost_display} />
        {job.duration_display && (
          <Meta label="Duration" value={job.duration_display} />
        )}
        {job.size_display && <Meta label="Source video" value={job.size_display} />}
      </div>

      {currentStage && !TERMINAL.has(status) && (
        <p className="muted" style={{ marginTop: 16 }}>
          Currently {currentStage} {stagePct}%
        </p>
      )}

      <ul className="stage-list" style={{ marginTop: 16 }}>
        {STAGES.map((s) => {
          const state = stageState(s.key, currentStage, status);
          return (
            <li key={s.key}>
              <span className={`stage-icon ${state}`}>
                {state === "done" ? "✓" : state === "failed" ? "✗" : ""}
              </span>
              <span style={{ color: state === "pending" ? "var(--muted)" : "var(--fg)" }}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ul>

      {failed && job.error && (
        <div className="error" style={{ marginTop: 16 }}>
          {job.error.code}: {job.error.message}
        </div>
      )}

      {succeeded && (
        <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {expired ? (
            <button
              className="primary"
              disabled
              title="Artifacts have been auto-deleted (7-day retention). Re-submit the source video to regenerate."
            >
              Download change.zip
            </button>
          ) : (
            <a href={api.downloadUrl(job.job_id)}>
              <button className="primary">Download change.zip</button>
            </a>
          )}
          <EvidenceDownloadButton jobId={job.job_id} status={bundleStatus} />
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="meta-cell">
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ marginTop: 2 }}>{value}</div>
    </div>
  );
}

function stageState(
  stage: string,
  currentStage: string | null,
  status: string,
): "pending" | "active" | "done" | "failed" {
  if (status === "succeeded" || status === "succeededwithwarnings") return "done";
  if (status === "failed" && currentStage === stage) return "failed";
  if (currentStage === null) return "pending";
  const order: string[] = STAGES.map((s) => s.key);
  const target = order.indexOf(stage);
  const here = order.indexOf(currentStage);
  if (here === -1) return "pending";
  if (target < here) return "done";
  if (target === here) return status === "failed" ? "failed" : "active";
  return "pending";
}
